import React from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { CheckCircle2, XCircle, AlertCircle, Pill } from 'lucide-react';
import { useCoverageVerification } from '../../../contexts/CoverageVerificationContext';

/**
 * CoverageSummaryCard Component
 * Displays the main coverage information matching the prototype design
 */
export const CoverageSummaryCard = ({ coverageData, showHeader = true }) => {
  const { setActiveTab } = useCoverageVerification();
  
  if (!coverageData) return null;

  const { drug, plan, coverage } = coverageData;

  /**
   * Visual status helpers
   */
  const getStatusIcon = () => {
    if (coverage.status === 'not_covered') return XCircle;
    if (coverage.paRequired || coverage.stRequired || coverage.qlApplies) return AlertCircle;
    return CheckCircle2;
  };

  const getStatusText = () => {
    if (coverage.status === 'not_covered') {
      if (coverage.medicalBenefit) {
        return 'Not Covered - Medical Benefit May Apply';
      }
      return 'Not Covered';
    }
    
    if (coverage.paRequired && coverage.stRequired) {
      return 'Covered - Restrictions Apply';
    }
    if (coverage.paRequired) {
      return 'Covered - PA Required';
    }
    if (coverage.stRequired) {
      return 'Covered - Step Therapy Required';
    }
    if (coverage.qlApplies) {
      return 'Covered - Quantity Limits Apply';
    }
    
    return 'Covered';
  };

  const StatusIcon = getStatusIcon();

  /**
   * Data field component for consistent styling
   */
  const DataField = ({ label, value }) => (
    <div className="space-y-1.5 sm:space-y-2">
      <div className="text-xs sm:text-sm font-semibold tracking-wide sm:tracking-wider text-foreground">{label}</div>
      <div className="text-sm sm:text-base font-normal text-foreground">{value || '—'}</div>
    </div>
  );

  return (
    <Card className="border-2 shadow-sm">
      {showHeader && (
        <CardHeader className="pb-1 px-4 sm:px-6">
          <CardTitle className="text-xl sm:text-2xl font-bold tracking-tight">
            Coverage Summary
          </CardTitle>
        </CardHeader>
      )}

      <CardContent className={`${showHeader ? 'p-4 sm:p-6 md:p-8' : 'p-0'} space-y-6 sm:space-y-8`}>
        {/* Primary Status Banner */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3 rounded-lg border">
          <StatusIcon 
            className={`h-5 w-5 sm:h-6 sm:w-6 shrink-0 ${
              coverage.status === 'not_covered' 
                ? 'text-status-not-covered' 
                : (coverage.paRequired || coverage.stRequired || coverage.qlApplies) 
                  ? 'text-status-restricted' 
                  : 'text-status-covered'
            }`} 
            strokeWidth={2.5} 
          />
          <div className="flex-1 min-w-0">
            <div className="text-xs sm:text-sm font-semibold tracking-wider text-foreground mb-2">
              Coverage Status
            </div>
            <div className={`text-base sm:text-lg font-bold ${
              coverage.status === 'not_covered' 
                ? 'text-status-not-covered' 
                : (coverage.paRequired || coverage.stRequired || coverage.qlApplies) 
                  ? 'text-status-restricted' 
                  : 'text-status-covered'
            }`}>
              {getStatusText()}
            </div>
            {coverage.medicalBenefit && (
              <div className="text-xs font-normal mt-1 text-muted-foreground">
                May be covered under medical benefit
              </div>
            )}
          </div>
          {coverage.unifiedTierName && (
            <div className="w-full sm:w-auto text-left sm:text-right pt-3 sm:pt-0 sm:pl-4 border-t sm:border-t-0 sm:border-l">
              <div className="text-xs sm:text-sm font-semibold tracking-wider text-foreground">
                Coverage Tier
              </div>
              <div className="flex items-center gap-2 mt-1">
                {coverage.unifiedTierColor && (
                  <div 
                    className="w-3 h-3 rounded-full border" 
                    style={{ 
                      backgroundColor: coverage.unifiedTierColor.startsWith('#') 
                        ? coverage.unifiedTierColor 
                        : `#${coverage.unifiedTierColor}` 
                    }}
                  />
                )}
                <div className="text-xs font-normal text-foreground">
                  {coverage.unifiedTierName}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Two-Column Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 sm:gap-x-8 md:gap-x-10 gap-y-6 sm:gap-y-8">
          {/* Left Column: Plan Information */}
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-x-6 sm:gap-x-8">
              <DataField label="Plan Type" value={plan.planType} />
              <DataField label="Channel" value={plan.channel} />
            </div>
            
            <div className="grid grid-cols-2 gap-x-6 sm:gap-x-8">
              {drug.doseForm && (
                <DataField label="Dose Form" value={drug.doseForm} />
              )}
              
              <DataField 
                label="OTC" 
                value={
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border-0 bg-status-not-covered text-white">
                    No
                  </span>
                }
              />
            </div>
            
            {drug.therapeuticClass && (
              <DataField label="Therapeutic Class" value={drug.therapeuticClass} />
            )}
          </div>

          {/* Right Column: Utilization Management */}
          <div className="space-y-6">
            <div className="space-y-3 sm:space-y-4">
              {/* Prior Authorization */}
              {(coverage.paRequired || coverage.conditionalPA) && (
                <div className="w-full flex items-start gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-lg border-2 border-status-restricted/30 bg-status-restricted/5">
                  <div className="mt-0.5 shrink-0">
                    <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-md text-[10px] sm:text-xs font-semibold bg-status-restricted text-white border-0">
                      PA
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs sm:text-sm font-semibold text-foreground">
                      Prior Authorization Required
                    </div>
                    <div className="text-[10px] sm:text-xs font-normal text-muted-foreground mt-1">
                      Prior Authorization may be required depending on the indication. View the Coverage Criteria tab for indication-specific requirements.
                    </div>
                  </div>
                </div>
              )}

              {/* Step Therapy */}
              {coverage.stRequired && (
                <div className="w-full flex items-start gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-lg border-2 border-status-restricted/30 bg-status-restricted/5">
                  <div className="mt-0.5 shrink-0">
                    <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-md text-[10px] sm:text-xs font-semibold bg-status-restricted text-white border-0">
                      ST
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs sm:text-sm font-semibold text-foreground">
                      Step Therapy Required
                    </div>
                    <div className="text-[10px] sm:text-xs font-normal text-muted-foreground mt-1">
                      Step therapy requirements may apply. View the Coverage Criteria tab for details.
                    </div>
                  </div>
                </div>
              )}

              {/* Quantity Limit */}
              {coverage.qlApplies && (
                <div className="w-full flex items-start gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-lg border-2 border-status-restricted/30 bg-status-restricted/5">
                  <div className="mt-0.5 shrink-0">
                    <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-md text-[10px] sm:text-xs font-semibold bg-status-restricted text-white border-0">
                      QL
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs sm:text-sm font-semibold text-foreground">
                      Quantity Limit Applies
                    </div>
                    <div className="text-[10px] sm:text-xs font-normal text-muted-foreground mt-1">
                      Quantity limits may apply. View the Coverage Criteria tab for details.
                    </div>
                  </div>
                </div>
              )}

              {/* Medical Benefit */}
              {coverage.medicalBenefit && (
                <div className="w-full flex items-start gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-lg border-2 border-status-info/30 bg-status-info/5">
                  <div className="mt-0.5 shrink-0">
                    <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-md text-[10px] sm:text-xs font-semibold bg-status-info text-white border-0">
                      MED
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs sm:text-sm font-semibold text-foreground">
                      Medical Benefit May Apply
                    </div>
                    <div className="text-[10px] sm:text-xs font-normal text-muted-foreground mt-1">
                      This drug may be covered under the medical benefit. Check with the plan for details.
                    </div>
                  </div>
                </div>
              )}

              {/* No Restrictions Listed */}
              {coverage.status === 'covered' && 
               !coverage.paRequired && 
               !coverage.conditionalPA && 
               !coverage.stRequired && 
               !coverage.qlApplies && 
               !coverage.medicalBenefit && (
                <div className="w-full flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-lg border-2 border-status-covered/30 bg-status-covered/5">
                  <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-status-covered shrink-0" />
                  <div className="text-xs sm:text-sm font-semibold text-foreground">
                    No Restrictions Listed
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t">
          {drug.drugMedId && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setActiveTab('drug-details')}
              className="flex items-center gap-2"
            >
              <Pill className="h-4 w-4" />
              View Drug Details
            </Button>
          )}
          <span className="text-xs font-normal text-muted-foreground ml-auto">
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};
